import {DecimalPipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, signal, viewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {SelectionModel} from '@angular/cdk/collections';
import {MatButtonModule} from '@angular/material/button';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MAT_DIALOG_DATA, MatDialogModule} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatPaginator, MatPaginatorModule} from '@angular/material/paginator';
import {MatSelectModule} from '@angular/material/select';
import {MatSort, MatSortModule} from '@angular/material/sort';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import * as echarts from 'echarts';
import {AverageCurve, AverageCurvePeriod, EdgarPipelineService} from '../../../services/edgar-pipeline.service';
import {
  CURVE_SAMPLE_COUNT,
  DEFAULT_IMPACT_DURATION_MINUTES,
  DEFAULT_RECOVERY_DURATION_MINUTES,
  piecewiseTau,
} from '../../utilities/outage-curve';

interface CompareDialogData {
  curves: AverageCurve[];
  periods: AverageCurvePeriod[];
  states: string[];
  selectedDatasetKey: string;
  periodLabel: string;
}

const MAX_SELECTION = 10;
const ALL_DATASETS = '__all_curve_datasets__';
const CHART_COLORS = ['#1d4ed8', '#dc2626', '#059669', '#d97706', '#7c3aed', '#0891b2', '#c026d3', '#be123c', '#0f766e', '#4338ca'];
type SortKey = 'impactLevel' | 'avgImpactDuration' | 'avgRecoveryDuration' | 'avgTauRise' |
  'avgTauFall' | 'totalDataPoints' | 'fipsCode' | 'countyName' | 'state';

@Component({
  selector: 'tgrid-county-curve-compare-dialog',
  imports: [
    DecimalPipe,
    MatButtonModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule,
    MatSelectModule,
    MatSortModule,
    MatTableModule,
  ],
  templateUrl: './county-curve-compare-dialog.component.html',
  styleUrl: './county-curve-compare-dialog.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountyCurveCompareDialogComponent {
  protected readonly data = inject<CompareDialogData>(MAT_DIALOG_DATA);
  private readonly pipeline = inject(EdgarPipelineService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly query = signal('');
  protected readonly normalized = signal(true);
  protected readonly selectedCurveKeys = signal<string[]>([]);
  protected readonly selection = new SelectionModel<AverageCurve>(true);
  protected readonly datasetFilter = signal('');
  protected readonly loadedCurves = signal<Record<string, AverageCurve[]>>({});
  protected readonly loadingDatasets = signal(false);
  protected readonly datasetError = signal('');
  protected readonly comparing = signal(false);
  protected readonly warning = signal('');
  protected readonly sortKey = signal<SortKey>('impactLevel');
  protected readonly sortDirection = signal<'desc' | 'asc'>('desc');
  protected readonly selectionColumns = [
    'select', 'fipsCode', 'countyName', 'state', 'dataset', 'avgImpactDuration', 'avgRecoveryDuration',
    'impactLevel', 'avgTauRise', 'avgTauFall', 'totalDataPoints',
  ];
  protected readonly detailColumns = this.selectionColumns.slice(1);
  protected readonly selectionDataSource = new MatTableDataSource<AverageCurve>();
  protected readonly chartElement = viewChild<ElementRef<HTMLDivElement>>('chart');
  private readonly paginator = viewChild(MatPaginator);
  private readonly tableSort = viewChild(MatSort);
  protected readonly availableCurves = computed(() => {
    const datasets = this.loadedCurves();
    if (this.datasetFilter() === ALL_DATASETS) {
      return this.data.periods.flatMap(period => datasets[period.datasetKey] ?? []);
    }
    return datasets[this.datasetFilter()] ?? [];
  });
  protected readonly filteredCurves = computed(() => {
    const query = this.query().trim().toLowerCase();
    const curves = this.availableCurves();
    return query ? curves.filter(curve => curve.fipsCode.includes(query) || curve.countyName.toLowerCase().includes(query) ||
      curve.state.toLowerCase().includes(query) || this.datasetLabel(curve.datasetKey).toLowerCase().includes(query)) : curves;
  });
  protected readonly selectedCurves = computed(() => {
    const selected = new Set(this.selectedCurveKeys());
    const key = this.sortKey();
    const direction = this.sortDirection() === 'desc' ? -1 : 1;
    return Object.values(this.loadedCurves()).flat()
      .filter(curve => selected.has(this.curveKey(curve)))
      .sort((left, right) => this.compareValues(left[key], right[key]) * direction);
  });
  private chart?: echarts.ECharts;
  private readonly loadingDatasetKeys = new Set<string>();

  public constructor() {
    const initialDatasetKey = this.data.selectedDatasetKey || this.data.curves[0]?.datasetKey || this.data.periods[0]?.datasetKey || '';
    this.datasetFilter.set(initialDatasetKey);
    if (initialDatasetKey) this.loadedCurves.set({[initialDatasetKey]: this.data.curves});
    this.selectionDataSource.sortingDataAccessor = (curve, column) => {
      if (column === 'dataset') return this.datasetLabel(curve.datasetKey);
      const value = curve[column as keyof AverageCurve];
      return typeof value === 'number' || typeof value === 'string' ? value : '';
    };
    effect(() => {
      this.selectionDataSource.data = this.filteredCurves();
      this.selectionDataSource.paginator?.firstPage();
    });
    effect(() => {
      const paginator = this.paginator();
      const sort = this.tableSort();
      if (paginator) this.selectionDataSource.paginator = paginator;
      if (sort) this.selectionDataSource.sort = sort;
    });
    effect(() => {
      const element = this.chartElement()?.nativeElement;
      const curves = this.selectedCurves();
      const normalized = this.normalized();
      if (!element || curves.length === 0) return;
      this.chart ??= echarts.init(element);
      this.chart.setOption(this.chartOptions(curves, normalized), true);
    });
  }

  protected updateQuery(value: string): void {
    this.query.set(value);
  }

  protected updateDatasetFilter(datasetKey: string): void {
    this.datasetFilter.set(datasetKey);
    this.loadDatasets(datasetKey === ALL_DATASETS ? this.data.periods.map(period => period.datasetKey) : [datasetKey]);
  }

  protected isSelected(curve: AverageCurve): boolean {
    return this.selection.isSelected(curve);
  }

  protected toggleCurve(curve: AverageCurve, checked: boolean): void {
    if (!checked) {
      this.selection.deselect(curve);
      this.syncSelectionKeys();
      this.warning.set('');
      return;
    }
    if (this.selection.selected.length >= MAX_SELECTION) {
      this.warning.set(`You can compare up to ${MAX_SELECTION} curves at once.`);
      return;
    }
    this.selection.select(curve);
    this.syncSelectionKeys();
    this.warning.set('');
  }

  protected clearSelection(): void {
    this.selection.clear();
    this.syncSelectionKeys();
    this.warning.set('');
    this.chart?.dispose();
    this.chart = undefined;
  }

  protected compareSelected(): void {
    if (this.selection.isEmpty()) {
      this.warning.set('Select at least one curve to compare.');
      return;
    }
    this.warning.set('');
    this.comparing.set(true);
  }

  protected editSelection(): void {
    this.comparing.set(false);
  }

  protected allPageSelected(): boolean {
    const rows = this.currentPageRows();
    return rows.length > 0 && rows.every(curve => this.selection.isSelected(curve));
  }

  protected togglePageSelection(checked: boolean): void {
    const rows = this.currentPageRows();
    if (!checked) {
      this.selection.deselect(...rows);
      this.syncSelectionKeys();
      this.warning.set('');
      return;
    }
    for (const curve of rows) {
      if (this.selection.selected.length >= MAX_SELECTION) {
        this.warning.set(`You can compare up to ${MAX_SELECTION} curves at once.`);
        break;
      }
      this.selection.select(curve);
    }
    this.syncSelectionKeys();
  }

  protected setSortKey(key: SortKey): void {
    this.sortKey.set(key);
  }

  protected setSortDirection(direction: 'desc' | 'asc'): void {
    this.sortDirection.set(direction);
  }

  protected datasetLabel(datasetKey: string): string {
    const period = this.data.periods.find(item => item.datasetKey === datasetKey);
    if (!period) return datasetKey || this.data.periodLabel;
    if (period.periodKind === 'all') {
      return `All years (${period.coverageStartDate.format('YYYY-MM-DD')} – ${period.coverageEndDate.format('YYYY-MM-DD')})`;
    }
    return `${period.periodYear} annual curve`;
  }

  protected curveKey(curve: AverageCurve): string {
    return `${curve.datasetKey}::${curve.fipsCode}`;
  }

  private chartOptions(curves: AverageCurve[], normalized: boolean): echarts.EChartsOption {
    const series = curves.map((curve, index) => {
      const impact = Math.max(1, curve.avgImpactDuration ?? DEFAULT_IMPACT_DURATION_MINUTES);
      const recovery = Math.max(1, curve.avgRecoveryDuration ?? DEFAULT_RECOVERY_DURATION_MINUTES);
      const countySize = Math.max(curve.countySize ?? 1, 1);
      const threshold = normalized ? (curve.threshold ?? 0) / countySize : curve.threshold ?? 0;
      const normalizedPeak = curve.impactLevelNormalized ?? curve.avgImpactLevelNormalized ?? 0;
      const peak = normalized ? normalizedPeak : curve.impactLevel ?? normalizedPeak * countySize;
      const points = Array.from({length: CURVE_SAMPLE_COUNT}, (_unused, pointIndex) => {
        const minute = -impact + (impact + recovery) * pointIndex / (CURVE_SAMPLE_COUNT - 1);
        return [minute, piecewiseTau(minute, -impact, threshold, 0, peak, recovery, threshold,
          curve.avgTauRise ?? 0, curve.avgTauFall ?? 0)];
      });
      const color = CHART_COLORS[index];
      const markLineData = [
        {yAxis: threshold, lineStyle: {color, type: 'dashed' as const, width: 1.4, opacity: 0.58}},
        ...(index === 0 ? [
          {xAxis: 0, lineStyle: {color: '#94a3b8', type: 'dashed' as const, width: 1}},
          {yAxis: 0, lineStyle: {color: '#94a3b8', type: 'dashed' as const, width: 1}},
        ] : []),
      ];
      return {
        type: 'line' as const,
        name: `${curve.fipsCode} ${curve.countyName}, ${curve.state} | ${this.datasetLabel(curve.datasetKey)}`,
        data: points,
        showSymbol: false,
        lineStyle: {color, width: 2.4},
        markLine: {symbol: 'none', silent: true, label: {show: false}, data: markLineData},
      };
    });
    return {
      animation: false,
      color: CHART_COLORS,
      grid: {top: 36, right: 360, bottom: 55, left: 74},
      legend: {type: 'scroll', orient: 'vertical', right: 8, top: 36, bottom: 36, width: 330, textStyle: {width: 285, overflow: 'break'}},
      tooltip: {trigger: 'axis', valueFormatter: value => Number(value).toLocaleString(undefined, {maximumFractionDigits: 4})},
      xAxis: {type: 'value', name: 'Time (min)', nameLocation: 'middle', nameGap: 34},
      yAxis: {
        type: 'value', name: normalized ? 'Outage Rate' : 'Impact Level', scale: true,
        axisLabel: {formatter: normalized ? (value: number) => value.toFixed(3) : (value: number) => value.toLocaleString()},
      },
      series,
    };
  }

  private compareValues(left: AverageCurve[SortKey], right: AverageCurve[SortKey]): number {
    if (left == null && right == null) return 0;
    if (left == null) return -1;
    if (right == null) return 1;
    if (typeof left === 'number' && typeof right === 'number') return left - right;
    return String(left).localeCompare(String(right), undefined, {numeric: true});
  }

  private currentPageRows(): AverageCurve[] {
    const paginator = this.selectionDataSource.paginator;
    const sort = this.selectionDataSource.sort;
    const ordered = sort
      ? this.selectionDataSource.sortData([...this.selectionDataSource.filteredData], sort)
      : this.selectionDataSource.filteredData;
    if (!paginator) return ordered;
    const start = paginator.pageIndex * paginator.pageSize;
    return ordered.slice(start, start + paginator.pageSize);
  }

  private syncSelectionKeys(): void {
    this.selectedCurveKeys.set(this.selection.selected.map(curve => this.curveKey(curve)));
  }

  private loadDatasets(datasetKeys: string[]): void {
    const loaded = this.loadedCurves();
    for (const datasetKey of datasetKeys) {
      if (!datasetKey || loaded[datasetKey] || this.loadingDatasetKeys.has(datasetKey)) continue;
      this.loadingDatasetKeys.add(datasetKey);
      this.loadingDatasets.set(true);
      this.pipeline.getAverageCurves(datasetKey, this.data.states).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: curves => {
          this.loadedCurves.update(current => ({...current, [datasetKey]: curves}));
          this.loadingDatasetKeys.delete(datasetKey);
          this.loadingDatasets.set(this.loadingDatasetKeys.size > 0);
        },
        error: () => {
          this.loadingDatasetKeys.delete(datasetKey);
          this.loadingDatasets.set(this.loadingDatasetKeys.size > 0);
          this.datasetError.set('Failed to load one or more curve datasets.');
        },
      });
    }
  }
}
